import { UiState, TabType, WobbleModeType } from '../state/UiState';
import { t } from '../i18n/i18n';

export interface PhysicsControlsPanelCallbacks {
  onTriggerPoke: () => void;
  onOpenSavePreset: () => void;
  onUpgradeRequested: (featureName: string) => void;
  onTabChanged?: (tab: TabType) => void;
  onPickWobblePos?: () => void;
  onAdjustWobbleRadius?: (radius: number) => void;
}

export class PhysicsControlsPanel {
  private element: HTMLElement;
  private uiState: UiState;
  private callbacks: PhysicsControlsPanelCallbacks;

  // タブ
  private tabPhysicsEl!: HTMLElement;
  private tabWobbleEl!: HTMLElement;
  private tabAudioEl!: HTMLElement;

  // 物理
  private labelRadiusEl!: HTMLElement;
  private labelStiffEl!: HTMLElement;
  private labelDampEl!: HTMLElement;
  private radiusSlider!: HTMLInputElement;
  private stiffnessSlider!: HTMLInputElement;
  private dampingSlider!: HTMLInputElement;
  private radiusVal!: HTMLElement;
  private stiffnessVal!: HTMLElement;
  private dampingVal!: HTMLElement;
  private btnPokeEl!: HTMLButtonElement;
  private btnResetEl!: HTMLButtonElement;
  private labelTouchRingEl!: HTMLElement;
  private chkShowTouchRing!: HTMLInputElement;
  private btnSavePresetOpen!: HTMLButtonElement;

  // 自動揺れ
  private lockWobbleEl!: HTMLElement;
  private lockWobbleBadgeEl!: HTMLElement;
  private lockWobbleDescEl!: HTMLElement;
  private btnUpgradeWobbleEl!: HTMLButtonElement;
  private labelAutoWobbleEnableEl!: HTMLElement;
  private chkAutoWobble!: HTMLInputElement;
  private labelWobbleModeEl!: HTMLElement;
  private btnModeBreath!: HTMLButtonElement;
  private btnModeHeartbeat!: HTMLButtonElement;
  private btnModeTremble!: HTMLButtonElement;
  private labelWobbleSpeedEl!: HTMLElement;
  private labelWobbleIntensityEl!: HTMLElement;
  private labelWobbleRadiusEl!: HTMLElement;
  private sliderWobbleSpeed!: HTMLInputElement;
  private sliderWobbleIntensity!: HTMLInputElement;
  private sliderWobbleRadius!: HTMLInputElement;
  private valWobbleSpeed!: HTMLElement;
  private valWobbleIntensity!: HTMLElement;
  private valWobbleRadius!: HTMLElement;
  private labelWobbleOriginEl!: HTMLElement;
  private valWobblePos!: HTMLElement;
  private btnToggleWobbleGuide!: HTMLButtonElement;
  private btnPickWobblePos!: HTMLButtonElement;
  private btnResetWobblePos!: HTMLButtonElement;
  private wobbleGuideNoteEl!: HTMLElement;

  constructor(element: HTMLElement, uiState: UiState, callbacks: PhysicsControlsPanelCallbacks) {
    this.element = element;
    this.uiState = uiState;
    this.callbacks = callbacks;
    this.render();
    this.initElements();
    this.bindEvents();

    this.uiState.subscribe((state) => {
      this.updateView(state);
    });
  }

  private render(): void {
    const texts = t();

    this.element.innerHTML = `
      <div class="panel-tabs">
        <div class="panel-tab active" id="tabHeadPhysics" data-tab="physics">${texts.physics.tabTitle}</div>
        <div class="panel-tab" id="tabHeadWobble" data-tab="wobble">${texts.wobble.tabTitle}</div>
        <div class="panel-tab" id="tabHeadAudio" data-tab="audio">${texts.audio.tabTitle}</div>
      </div>

      <!-- タブ1: 物理パラメータ -->
      <div class="tab-content active" id="tabPhysics">
        <div class="control-group">
          <div class="control-header">
            <span id="labelRadius">${texts.physics.radius}</span>
            <span class="control-value" id="radiusVal">28%</span>
          </div>
          <div class="slider-container">
            <span class="slider-bound-label">0%</span>
            <input type="range" id="radiusSlider" min="5" max="30" value="12" class="param-slider">
            <span class="slider-bound-label">100%</span>
          </div>
        </div>

        <div class="control-group">
          <div class="control-header">
            <span id="labelStiff">${texts.physics.stiffness}</span>
            <span class="control-value" id="stiffnessVal">50%</span>
          </div>
          <div class="slider-container">
            <span class="slider-bound-label">0%</span>
            <input type="range" id="stiffnessSlider" min="10" max="90" value="50" class="param-slider">
            <span class="slider-bound-label">100%</span>
          </div>
        </div>

        <div class="control-group">
          <div class="control-header">
            <span id="labelDamp">${texts.physics.damping}</span>
            <span class="control-value" id="dampingVal">69%</span>
          </div>
          <div class="slider-container">
            <span class="slider-bound-label">0%</span>
            <input type="range" id="dampingSlider" min="30" max="95" value="75" class="param-slider">
            <span class="slider-bound-label">100%</span>
          </div>
        </div>

        <div style="display: flex; gap: 8px; margin-top: 14px;">
          <button class="btn btn-secondary" id="btnPoke" style="flex:1;">${texts.physics.poke}</button>
          <button class="btn btn-secondary" id="btnResetPhysics" style="flex:1;">${texts.physics.reset}</button>
        </div>
        <div class="control-group" style="display: flex; justify-content: space-between; align-items: center; margin-top: 14px; padding-top: 10px; border-top: 1px dashed var(--color-card-border);">
          <span id="labelTouchRing" style="font-weight: 600; font-size: 13px;">${texts.physics.touchRing}</span>
          <input type="checkbox" id="chkShowTouchRing" checked>
        </div>
        <button class="btn btn-primary" id="btnSavePresetOpen" style="width: 100%; margin-top: 10px;">
          ${texts.physics.savePreset}
        </button>
      </div>

      <!-- タブ2: 自動揺れモード -->
      <div class="tab-content pro-locked-section" id="tabWobble">
        <div class="pro-lock-overlay" id="lockWobble">
          <div class="pro-lock-badge" id="lockWobbleBadge">🔒 ${texts.upgrade.title}</div>
          <p id="lockWobbleDesc" style="font-size: 12px; color: var(--color-text-muted);">${texts.upgrade.perkWobble}</p>
          <button class="btn btn-primary" id="btnUpgradeWobble" style="padding: 6px 14px; font-size: 12px;">
            ${texts.upgrade.ok}
          </button>
        </div>

        <div class="control-group" style="display: flex; justify-content: space-between; align-items: center;">
          <span id="labelAutoWobbleEnable" style="font-weight: 600;">${texts.wobble.enable}</span>
          <input type="checkbox" id="chkAutoWobble">
        </div>

        <div class="control-group">
          <label id="labelWobbleMode" style="font-size: 12px; font-weight:600; display:block; margin-bottom:6px;">${texts.wobble.mode}</label>
          <div style="display: flex; gap: 6px;">
            <button class="btn btn-secondary mode-btn active" id="btnModeBreath" data-mode="breath">${texts.wobble.breath}</button>
            <button class="btn btn-secondary mode-btn" id="btnModeHeartbeat" data-mode="heartbeat">${texts.wobble.heartbeat}</button>
            <button class="btn btn-secondary mode-btn" id="btnModeTremble" data-mode="tremble">${texts.wobble.tremble}</button>
          </div>
        </div>

        <div class="control-group">
          <div class="control-header">
            <span id="labelWobbleSpeed">${texts.wobble.speed}</span>
            <span class="control-value" id="valWobbleSpeed">1.0x</span>
          </div>
          <input type="range" id="sliderWobbleSpeed" min="0.5" max="2.0" step="0.1" value="1.0">
        </div>

        <div class="control-group">
          <div class="control-header">
            <span id="labelWobbleIntensity">${texts.wobble.intensity}</span>
            <span class="control-value" id="valWobbleIntensity">0.30</span>
          </div>
          <input type="range" id="sliderWobbleIntensity" min="0.1" max="1.0" step="0.05" value="0.30">
        </div>

        <div class="control-group">
          <div class="control-header">
            <span id="labelWobbleRadius">${texts.wobble.radius}</span>
            <span class="control-value" id="valWobbleRadius">33%</span>
          </div>
          <div class="slider-container">
            <span class="slider-bound-label">0%</span>
            <input type="range" id="sliderWobbleRadius" min="5" max="45" step="1" value="18" class="param-slider">
            <span class="slider-bound-label">100%</span>
          </div>
        </div>

        <div class="control-group" style="padding-top: 10px; border-top: 1px dashed var(--color-card-border);">
          <div class="control-header">
            <span id="labelWobbleOrigin">${texts.wobble.origin}</span>
            <span class="control-value" id="valWobblePos">${texts.wobble.centerLabel}</span>
          </div>
          <div style="display: flex; gap: 6px; margin-top: 6px;">
            <button class="btn btn-secondary" id="btnToggleWobbleGuide" style="flex: 1.2; font-size: 12px; padding: 6px 8px;">
              ${texts.wobble.showGuide}
            </button>
            <button class="btn btn-secondary" id="btnPickWobblePos" style="flex: 1.2; font-size: 12px; padding: 6px 8px;">
              ${texts.wobble.pickPos}
            </button>
            <button class="btn btn-secondary" id="btnResetWobblePos" style="flex: 0.8; font-size: 12px; padding: 6px 8px;">
              ${texts.wobble.resetCenter}
            </button>
          </div>
          <p id="wobbleGuideNote" style="font-size: 11px; color: var(--color-text-muted); margin-top: 6px; line-height: 1.4;">
            ${texts.wobble.guideNote}
          </p>
        </div>
      </div>

      <!-- タブ3: オーディオ設定プレースホルダー -->
      <div class="tab-content pro-locked-section" id="tabAudio">
      </div>

      <!-- パネル最下部 フッター（法的表記＆お問い合わせ） -->
      <div class="panel-footer" id="panelLegalFooter" style="margin-top: auto; padding: 10px 14px; border-top: 1px solid var(--color-card-border); background: #faf8f5; font-size: 11px; color: var(--color-text-muted); display: flex; flex-wrap: wrap; align-items: center; justify-content: center; gap: 4px 8px; text-align: center; flex-shrink: 0;">
        <a href="/privacy.html" id="panelLinkPrivacy" style="color: var(--color-text-muted); text-decoration: underline; cursor: pointer;">${texts.legal.footerPrivacy}</a>
        <span style="opacity: 0.5;">•</span>
        <a href="/terms.html" id="panelLinkTerms" style="color: var(--color-text-muted); text-decoration: underline; cursor: pointer;">${texts.legal.footerTerms}</a>
        <span style="opacity: 0.5;">•</span>
        <a href="https://x.com/KILROYSANWHERE" target="_blank" rel="noopener noreferrer" style="color: var(--color-text-muted); text-decoration: underline; font-weight: 600;">𝕏 @KILROYSANWHERE</a>
      </div>
    `;
  }

  private initElements(): void {
    this.tabPhysicsEl = this.element.querySelector('#tabHeadPhysics') as HTMLElement;
    this.tabWobbleEl = this.element.querySelector('#tabHeadWobble') as HTMLElement;
    this.tabAudioEl = this.element.querySelector('#tabHeadAudio') as HTMLElement;

    this.labelRadiusEl = this.element.querySelector('#labelRadius') as HTMLElement;
    this.labelStiffEl = this.element.querySelector('#labelStiff') as HTMLElement;
    this.labelDampEl = this.element.querySelector('#labelDamp') as HTMLElement;
    this.radiusSlider = this.element.querySelector('#radiusSlider') as HTMLInputElement;
    this.stiffnessSlider = this.element.querySelector('#stiffnessSlider') as HTMLInputElement;
    this.dampingSlider = this.element.querySelector('#dampingSlider') as HTMLInputElement;
    this.radiusVal = this.element.querySelector('#radiusVal') as HTMLElement;
    this.stiffnessVal = this.element.querySelector('#stiffnessVal') as HTMLElement;
    this.dampingVal = this.element.querySelector('#dampingVal') as HTMLElement;
    this.btnPokeEl = this.element.querySelector('#btnPoke') as HTMLButtonElement;
    this.btnResetEl = this.element.querySelector('#btnResetPhysics') as HTMLButtonElement;
    this.labelTouchRingEl = this.element.querySelector('#labelTouchRing') as HTMLElement;
    this.chkShowTouchRing = this.element.querySelector('#chkShowTouchRing') as HTMLInputElement;
    this.btnSavePresetOpen = this.element.querySelector('#btnSavePresetOpen') as HTMLButtonElement;

    this.lockWobbleEl = this.element.querySelector('#lockWobble') as HTMLElement;
    this.lockWobbleBadgeEl = this.element.querySelector('#lockWobbleBadge') as HTMLElement;
    this.lockWobbleDescEl = this.element.querySelector('#lockWobbleDesc') as HTMLElement;
    this.btnUpgradeWobbleEl = this.element.querySelector('#btnUpgradeWobble') as HTMLButtonElement;
    this.labelAutoWobbleEnableEl = this.element.querySelector('#labelAutoWobbleEnable') as HTMLElement;
    this.chkAutoWobble = this.element.querySelector('#chkAutoWobble') as HTMLInputElement;
    this.labelWobbleModeEl = this.element.querySelector('#labelWobbleMode') as HTMLElement;
    this.btnModeBreath = this.element.querySelector('#btnModeBreath') as HTMLButtonElement;
    this.btnModeHeartbeat = this.element.querySelector('#btnModeHeartbeat') as HTMLButtonElement;
    this.btnModeTremble = this.element.querySelector('#btnModeTremble') as HTMLButtonElement;
    this.labelWobbleSpeedEl = this.element.querySelector('#labelWobbleSpeed') as HTMLElement;
    this.labelWobbleIntensityEl = this.element.querySelector('#labelWobbleIntensity') as HTMLElement;
    this.labelWobbleRadiusEl = this.element.querySelector('#labelWobbleRadius') as HTMLElement;
    this.sliderWobbleSpeed = this.element.querySelector('#sliderWobbleSpeed') as HTMLInputElement;
    this.sliderWobbleIntensity = this.element.querySelector('#sliderWobbleIntensity') as HTMLInputElement;
    this.sliderWobbleRadius = this.element.querySelector('#sliderWobbleRadius') as HTMLInputElement;
    this.valWobbleSpeed = this.element.querySelector('#valWobbleSpeed') as HTMLElement;
    this.valWobbleIntensity = this.element.querySelector('#valWobbleIntensity') as HTMLElement;
    this.valWobbleRadius = this.element.querySelector('#valWobbleRadius') as HTMLElement;
    this.labelWobbleOriginEl = this.element.querySelector('#labelWobbleOrigin') as HTMLElement;
    this.valWobblePos = this.element.querySelector('#valWobblePos') as HTMLElement;
    this.btnToggleWobbleGuide = this.element.querySelector('#btnToggleWobbleGuide') as HTMLButtonElement;
    this.btnPickWobblePos = this.element.querySelector('#btnPickWobblePos') as HTMLButtonElement;
    this.btnResetWobblePos = this.element.querySelector('#btnResetWobblePos') as HTMLButtonElement;
    this.wobbleGuideNoteEl = this.element.querySelector('#wobbleGuideNote') as HTMLElement;
  }

  private bindEvents(): void {
    // タブ切り替え
    this.element.querySelectorAll<HTMLElement>('.panel-tab').forEach((tabEl) => {
      tabEl.addEventListener('click', () => {
        const tab = tabEl.getAttribute('data-tab') as TabType;
        if (tab) {
          this.switchTab(tab);
          this.callbacks.onTabChanged?.(tab);
        }
      });
    });

    // 物理スライダー
    const onSliderChange = () => {
      const r = parseInt(this.radiusSlider.value, 10);
      const s = parseInt(this.stiffnessSlider.value, 10);
      const d = parseInt(this.dampingSlider.value, 10);
      this.radiusVal.textContent = this.formatPercent(r, 5, 30);
      this.stiffnessVal.textContent = this.formatPercent(s, 10, 90);
      this.dampingVal.textContent = this.formatPercent(d, 30, 95);
      this.uiState.setPhysicsSliders(r, s, d);
    };

    this.radiusSlider.addEventListener('input', onSliderChange);
    this.stiffnessSlider.addEventListener('input', onSliderChange);
    this.dampingSlider.addEventListener('input', onSliderChange);

    // アクションボタン
    this.btnPokeEl?.addEventListener('click', () => {
      this.callbacks.onTriggerPoke();
    });

    this.btnResetEl?.addEventListener('click', () => {
      this.uiState.resetPhysicsDefault();
      this.callbacks.onTriggerPoke();
    });

    // タッチ位置の強調表示切り替え
    this.chkShowTouchRing.addEventListener('change', () => {
      this.uiState.setTouchRingVisible(this.chkShowTouchRing.checked);
    });

    // プリセット保存
    this.btnSavePresetOpen.addEventListener('click', () => {
      const texts = t();
      if (!this.uiState.isPresetSaveAllowed()) {
        this.callbacks.onUpgradeRequested(texts.upgrade.perkPreset);
        return;
      }
      this.callbacks.onOpenSavePreset();
    });

    // 自動揺れ
    this.chkAutoWobble.addEventListener('change', () => {
      this.uiState.setAutoWobble(this.chkAutoWobble.checked);
    });

    const setMode = (mode: WobbleModeType) => {
      this.uiState.setAutoWobble(this.uiState.getSnapshot().autoWobble.enabled, mode);
    };

    this.btnModeBreath.addEventListener('click', () => setMode('breath'));
    this.btnModeHeartbeat.addEventListener('click', () => setMode('heartbeat'));
    this.btnModeTremble.addEventListener('click', () => setMode('tremble'));

    const onAutoWobbleParamChange = () => {
      const speed = parseFloat(this.sliderWobbleSpeed.value);
      const intensity = parseFloat(this.sliderWobbleIntensity.value);
      this.uiState.setAutoWobble(this.chkAutoWobble.checked, undefined, speed, intensity);
    };

    this.sliderWobbleSpeed.addEventListener('input', onAutoWobbleParamChange);
    this.sliderWobbleIntensity.addEventListener('input', onAutoWobbleParamChange);

    this.sliderWobbleRadius.addEventListener('input', () => {
      const r = parseInt(this.sliderWobbleRadius.value, 10);
      this.valWobbleRadius.textContent = this.formatPercent(r, 5, 45);
      this.uiState.setAutoWobbleRadius(r);
      this.callbacks.onAdjustWobbleRadius?.(r);
    });

    this.btnUpgradeWobbleEl?.addEventListener('click', () => {
      const texts = t();
      this.callbacks.onUpgradeRequested(texts.upgrade.perkWobble);
    });

    // ガイド表示トグルボタン
    this.btnToggleWobbleGuide?.addEventListener('click', () => {
      const curGuide = this.uiState.getSnapshot().autoWobble.showGuide;
      this.uiState.setAutoWobbleGuide(!curGuide);
    });

    // 揺らす位置指定ボタン
    this.btnPickWobblePos?.addEventListener('click', () => {
      const texts = t().wobble;
      this.btnPickWobblePos.textContent = texts.pickingPos;
      this.callbacks.onPickWobblePos?.();
    });

    this.btnResetWobblePos?.addEventListener('click', () => {
      const texts = t().wobble;
      this.uiState.setAutoWobbleCenter(0.5, 0.55);
      this.btnPickWobblePos.textContent = texts.pickPos;
    });
  }

  switchTab(targetTab: TabType): void {
    this.uiState.setActiveTab(targetTab);
    this.element.querySelectorAll('.panel-tab').forEach((tabEl) => {
      tabEl.classList.toggle('active', tabEl.getAttribute('data-tab') === targetTab);
    });

    const tabs: TabType[] = ['physics', 'wobble', 'audio'];
    tabs.forEach((tab) => {
      const content = this.element.querySelector(`#tab${tab.charAt(0).toUpperCase() + tab.slice(1)}`);
      content?.classList.toggle('active', tab === targetTab);
    });
  }

  private formatPercent(val: number, min: number, max: number): string {
    const clamped = Math.min(max, Math.max(min, val));
    const pct = Math.round(((clamped - min) / (max - min)) * 100);
    return `${pct}%`;
  }

  private updateView(state: ReturnType<UiState['getSnapshot']>): void {
    const texts = t();

    // タブ名更新
    if (this.tabPhysicsEl) this.tabPhysicsEl.textContent = texts.physics.tabTitle;
    if (this.tabWobbleEl) this.tabWobbleEl.textContent = texts.wobble.tabTitle;
    if (this.tabAudioEl) this.tabAudioEl.textContent = texts.audio.tabTitle;

    // 物理ラベル更新
    if (this.labelRadiusEl) this.labelRadiusEl.textContent = texts.physics.radius;
    if (this.labelStiffEl) this.labelStiffEl.textContent = texts.physics.stiffness;
    if (this.labelDampEl) this.labelDampEl.textContent = texts.physics.damping;
    if (this.btnPokeEl) this.btnPokeEl.textContent = texts.physics.poke;
    if (this.btnResetEl) this.btnResetEl.textContent = texts.physics.reset;
    if (this.labelTouchRingEl) this.labelTouchRingEl.textContent = texts.physics.touchRing;

    // 物理スライダー値同期
    this.radiusSlider.value = state.physics.radiusSlider.toString();
    this.stiffnessSlider.value = state.physics.stiffnessSlider.toString();
    this.dampingSlider.value = state.physics.dampingSlider.toString();

    this.radiusVal.textContent = this.formatPercent(state.physics.radiusSlider, 5, 30);
    this.stiffnessVal.textContent = this.formatPercent(state.physics.stiffnessSlider, 10, 90);
    this.dampingVal.textContent = this.formatPercent(state.physics.dampingSlider, 30, 95);

    if (this.chkShowTouchRing) {
      this.chkShowTouchRing.checked = state.touchRingVisible;
    }

    if (this.btnSavePresetOpen) {
      const isPresetAllowed = this.uiState.isPresetSaveAllowed();
      this.btnSavePresetOpen.innerHTML = isPresetAllowed ? texts.physics.savePreset : texts.physics.savePresetLocked;
      this.btnSavePresetOpen.classList.toggle('btn-primary', isPresetAllowed);
      this.btnSavePresetOpen.classList.toggle('btn-secondary', !isPresetAllowed);
    }

    // 自動揺れラベル更新
    if (this.lockWobbleBadgeEl) this.lockWobbleBadgeEl.textContent = `🔒 ${texts.upgrade.title}`;
    if (this.lockWobbleDescEl) this.lockWobbleDescEl.textContent = texts.upgrade.perkWobble;
    if (this.btnUpgradeWobbleEl) this.btnUpgradeWobbleEl.textContent = texts.upgrade.ok;
    if (this.labelAutoWobbleEnableEl) this.labelAutoWobbleEnableEl.textContent = texts.wobble.enable;
    if (this.labelWobbleModeEl) this.labelWobbleModeEl.textContent = texts.wobble.mode;
    if (this.btnModeBreath) this.btnModeBreath.textContent = texts.wobble.breath;
    if (this.btnModeHeartbeat) this.btnModeHeartbeat.textContent = texts.wobble.heartbeat;
    if (this.btnModeTremble) this.btnModeTremble.textContent = texts.wobble.tremble;
    if (this.labelWobbleSpeedEl) this.labelWobbleSpeedEl.textContent = texts.wobble.speed;
    if (this.labelWobbleIntensityEl) this.labelWobbleIntensityEl.textContent = texts.wobble.intensity;
    if (this.labelWobbleRadiusEl) this.labelWobbleRadiusEl.textContent = texts.wobble.radius;
    if (this.labelWobbleOriginEl) this.labelWobbleOriginEl.textContent = texts.wobble.origin;
    if (this.btnResetWobblePos) this.btnResetWobblePos.textContent = texts.wobble.resetCenter;
    if (this.wobbleGuideNoteEl) this.wobbleGuideNoteEl.textContent = texts.wobble.guideNote;

    // 自動揺れ同期
    const isWobbleAllowed = this.uiState.isAutoWobbleAllowed();
    this.lockWobbleEl.style.display = isWobbleAllowed ? 'none' : 'flex';

    this.chkAutoWobble.checked = state.autoWobble.enabled;
    this.btnModeBreath.classList.toggle('active', state.autoWobble.mode === 'breath');
    this.btnModeHeartbeat.classList.toggle('active', state.autoWobble.mode === 'heartbeat');
    this.btnModeTremble.classList.toggle('active', state.autoWobble.mode === 'tremble');

    this.sliderWobbleSpeed.value = state.autoWobble.speed.toString();
    this.sliderWobbleIntensity.value = state.autoWobble.intensity.toString();
    this.valWobbleSpeed.textContent = state.autoWobble.speed.toFixed(1) + 'x';
    this.valWobbleIntensity.textContent = state.autoWobble.intensity.toFixed(2);

    if (this.sliderWobbleRadius) {
      const r = state.autoWobble.radiusSlider ?? 18;
      this.sliderWobbleRadius.value = r.toString();
      this.valWobbleRadius.textContent = this.formatPercent(r, 5, 45);
    }

    if (this.btnToggleWobbleGuide) {
      const showGuide = state.autoWobble.showGuide;
      this.btnToggleWobbleGuide.innerHTML = showGuide ? texts.wobble.hideGuide : texts.wobble.showGuide;
      this.btnToggleWobbleGuide.classList.toggle('active', showGuide);
    }

    if (this.valWobblePos) {
      const cx = Math.round((state.autoWobble.centerX ?? 0.5) * 100);
      const cy = Math.round((state.autoWobble.centerY ?? 0.55) * 100);
      const isCenter = cx === 50 && cy === 55;
      this.valWobblePos.textContent = isCenter ? texts.wobble.centerLabel : `(${cx}%, ${cy}%)`;
    }
    if (this.btnPickWobblePos && this.btnPickWobblePos.textContent !== texts.wobble.pickingPos) {
      this.btnPickWobblePos.textContent = texts.wobble.pickPos;
    }
    const linkPriv = this.element.querySelector('#panelLinkPrivacy');
    if (linkPriv) linkPriv.textContent = texts.legal.footerPrivacy;
    const linkTerms = this.element.querySelector('#panelLinkTerms');
    if (linkTerms) linkTerms.textContent = texts.legal.footerTerms;
  }
}
